import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
    Bot, FolderKanban, Inbox, Loader2, LogOut, Map, Plus, Presentation as PresentationIcon,
} from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { ProjectDialog } from "../components/Modals";

function timeAgo(iso) {
    if (!iso) return "–";
    const diff = Date.now() - new Date(iso).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return "nu";
    if (mins < 60) return `${mins} min sedan`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours} tim sedan`;
    return `${Math.floor(hours / 24)} dagar sedan`;
}

export default function Overview() {
    const [data, setData] = useState(null);
    const [showNew, setShowNew] = useState(false);
    const { user, logout } = useAuth();
    const navigate = useNavigate();

    const load = () => api.get("/overview").then((r) => setData(r.data)).catch((e) => toast.error(formatApiError(e, "Kunde inte ladda överblicken")));
    useEffect(() => { load(); }, []);

    if (!data) {
        return <div className="min-h-screen bg-abyss flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-current" /></div>;
    }
    const t = data.totals;

    return (
        <div className="min-h-screen canvas-bg text-slate-100" data-testid="overview-page">
            <header className="h-16 border-b border-border bg-canvas/80 backdrop-blur-md flex items-center gap-3 px-4 xl:px-6">
                <img src="/hamn-emblem.png" alt="HAMN" className="w-8 h-8 rounded-full border border-current/30 object-cover" />
                <div>
                    <h1 className="font-display font-extrabold text-xl tracking-tight">HAMN ATLAS</h1>
                    <p className="font-mono text-[9px] uppercase tracking-widest text-slate-500">Överblick · samarbeten & kartläggningar</p>
                </div>
                <div className="ml-auto flex items-center gap-3">
                    <span className="font-mono text-[10px] text-slate-500 hidden md:block">{user?.email}</span>
                    <button data-testid="overview-logout-button" onClick={logout} title="Logga ut"
                        className="p-2 rounded-lg text-slate-400 hover:text-red-400 hover:bg-red-500/10 transition-colors">
                        <LogOut className="w-4 h-4" />
                    </button>
                </div>
            </header>

            <main className="max-w-6xl mx-auto px-4 xl:px-6 py-6 space-y-6">
                <div className="grid grid-cols-3 gap-3">
                    <div className="glass-panel rounded-2xl p-4" data-testid="kpi-projects">
                        <p className="font-mono text-[9px] uppercase tracking-widest text-slate-500">Projekt</p>
                        <p className="font-display font-extrabold text-3xl mt-1">{t.projects}</p>
                    </div>
                    <div className="glass-panel rounded-2xl p-4" data-testid="kpi-agents">
                        <p className="font-mono text-[9px] uppercase tracking-widest text-slate-500">Aktiva rollagenter</p>
                        <p className="font-display font-extrabold text-3xl mt-1 text-current">{t.active_agents}</p>
                    </div>
                    <div className="glass-panel rounded-2xl p-4" data-testid="kpi-unread">
                        <p className="font-mono text-[9px] uppercase tracking-widest text-slate-500">Olästa meddelanden</p>
                        <p className={`font-display font-extrabold text-3xl mt-1 ${t.unread > 0 ? "text-violet-300" : ""}`}>{t.unread}</p>
                    </div>
                </div>

                {data.inbox.length > 0 && (
                    <section className="rounded-2xl border border-violet-500/40 bg-violet-500/10 p-4" data-testid="overview-inbox">
                        <p className="font-mono text-[10px] uppercase tracking-widest text-violet-300 mb-3 flex items-center gap-1.5">
                            <Inbox className="w-4 h-4" /> Meddelanden från deltagare
                        </p>
                        <div className="space-y-2">
                            {data.inbox.map((item) => (
                                <button key={item.agent_id} data-testid={`overview-inbox-item-${item.agent_id}`}
                                    onClick={() => navigate(`/rollspegling/${item.project_id}?agent=${item.agent_id}`)}
                                    className="w-full text-left rounded-xl border border-violet-500/20 bg-abyss/50 px-3 py-2.5 hover:border-violet-400/50 transition-colors">
                                    <div className="flex items-center justify-between gap-2">
                                        <span className="text-sm font-semibold text-slate-100">{item.participant_name} <span className="font-normal text-slate-400">· {item.role_label}</span></span>
                                        <span className="font-mono text-[9px] text-violet-300 shrink-0">{item.project_name} · {timeAgo(item.at)}</span>
                                    </div>
                                    {item.preview && <p className="text-xs text-slate-400 truncate mt-0.5">{item.preview}</p>}
                                </button>
                            ))}
                        </div>
                    </section>
                )}

                <section>
                    <div className="flex items-center justify-between mb-3">
                        <h2 className="font-display font-bold text-lg flex items-center gap-2"><FolderKanban className="w-4 h-4 text-current" /> Dina projekt</h2>
                        <button data-testid="overview-new-project-button" onClick={() => setShowNew(true)}
                            className="flex items-center gap-1.5 rounded-lg bg-current text-abyss px-3 py-2 text-xs font-semibold hover:bg-tide transition-colors">
                            <Plus className="w-3.5 h-3.5" /> Nytt projekt
                        </button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {data.projects.map((p) => (
                            <div key={p.id} data-testid={`project-card-${p.id}`}
                                className="glass-panel rounded-2xl p-5 flex flex-col gap-3 hover:border-current/40 transition-colors">
                                <div className="flex items-start justify-between gap-2">
                                    <div>
                                        <h3 className="font-display font-bold text-lg leading-snug">{p.name}</h3>
                                        {p.description && <p className="text-xs text-slate-500 mt-1 line-clamp-2">{p.description}</p>}
                                    </div>
                                    {p.demo && <span className="font-mono text-[9px] uppercase tracking-widest text-amber-300 border border-amber-500/40 bg-amber-500/10 rounded-full px-2 py-0.5 shrink-0">Demo</span>}
                                </div>
                                <div className="flex items-center gap-3 font-mono text-[10px] uppercase tracking-widest text-slate-500 flex-wrap">
                                    <span>{p.node_count} noder</span>
                                    <span>{p.active_agents} aktiva agenter</span>
                                    {p.unread > 0 && <span className="text-violet-300">{p.unread} olästa</span>}
                                    <span className="ml-auto normal-case tracking-normal">Aktivitet {timeAgo(p.last_activity)}</span>
                                </div>
                                <div className="flex items-center gap-2 mt-auto">
                                    <button data-testid={`open-project-${p.id}`} onClick={() => navigate(`/karta?projekt=${p.id}`)}
                                        className="flex-1 flex items-center justify-center gap-1.5 rounded-lg bg-current text-abyss py-2 text-xs font-semibold hover:bg-tide transition-colors">
                                        <Map className="w-3.5 h-3.5" /> Öppna kartan
                                    </button>
                                    <button data-testid={`project-rollspegling-${p.id}`} onClick={() => navigate(`/rollspegling/${p.id}`)} title="Rollspegling"
                                        className="p-2 rounded-lg border border-border text-slate-400 hover:text-current hover:border-current/50 transition-colors">
                                        <Bot className="w-4 h-4" />
                                    </button>
                                    <button data-testid={`project-dashboard-${p.id}`} onClick={() => navigate(`/view/${p.id}`)} title="Executive Dashboard"
                                        className="p-2 rounded-lg border border-border text-slate-400 hover:text-buoy hover:border-buoy/50 transition-colors">
                                        <PresentationIcon className="w-4 h-4" />
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </section>

                <section className="glass-panel rounded-2xl p-4" data-testid="activity-feed">
                    <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mb-3">Senaste aktivitet</p>
                    <div className="space-y-1.5">
                        {data.recent_activity.length === 0 && <p className="text-xs text-slate-600">Ingen aktivitet ännu.</p>}
                        {data.recent_activity.map((a, i) => (
                            <div key={a.id || i} data-testid={`activity-item-${i}`} className="flex items-baseline gap-2 text-xs">
                                <span className="font-mono text-[10px] text-slate-600 shrink-0 w-24">{timeAgo(a.at)}</span>
                                <span className="text-slate-300"><span className="text-slate-500">{a.actor}</span> {a.action}{a.detail ? `: ${a.detail}` : ""}</span>
                                {a.project_name && <span className="font-mono text-[9px] text-slate-600 ml-auto shrink-0">{a.project_name}</span>}
                            </div>
                        ))}
                    </div>
                </section>
            </main>

            <ProjectDialog open={showNew} onClose={() => setShowNew(false)}
                onCreated={(p) => { setShowNew(false); navigate(`/karta?projekt=${p.id}`); }} />
        </div>
    );
}
